import { FormValues } from '@/lib/types';
import type { ParsedValues } from '@/components/sop-intake/sop-analyzer/types';
import { getSopAssessmentByKey } from '@/components/sop-intake/sop-analyzer/constants';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';

const isBlank = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  value === 'Select...';

const parseFloatString = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const str = String(value).trim();
  if (str === '' || str === 'Select...') return null;
  const match = str.match(/-?\d+(?:\.\d+)?/);
  return match ? parseFloat(match[0]) : null;
};

const parseGroundingMode = (value: unknown): 'dynamometer' | 'perceived' => {
  if (typeof value === 'string' && value.toLowerCase() === 'perceived') {
    return 'perceived';
  }
  return 'dynamometer';
};

const parseGroundingValueByMode = (
  formValues: FormValues,
  side: 'L' | 'R',
  groundingMode: 'dynamometer' | 'perceived'
): number | null => {
  const actualKeys = [`${side}_GroundingPerformance_actual`, `${side}_GroundingPerformanceActual`];
  const perceivedKeys = [`${side}_GroundingPerformance_perceived`, `${side}_GroundingPerformancePerceived`];
  const fallbackKeys = [`${side}_GroundingPerformance`];

  const preferredKeys = groundingMode === 'perceived' ? perceivedKeys : actualKeys;
  const secondaryKeys = groundingMode === 'perceived' ? actualKeys : perceivedKeys;

  for (const key of preferredKeys) {
    const parsed = parseFloatString(formValues[key]);
    if (parsed !== null) return parsed;
  }
  for (const key of secondaryKeys) {
    const parsed = parseFloatString(formValues[key]);
    if (parsed !== null) return parsed;
  }
  for (const key of fallbackKeys) {
    const parsed = parseFloatString(formValues[key]);
    if (parsed !== null) return parsed;
  }
  return null;
};

const parseGroundingDetailed = (
  formValues: FormValues,
  side: 'L' | 'R'
): { actual: number | null; perceived: number | null } => {
  const actualKeys = [`${side}_GroundingPerformance_actual`, `${side}_GroundingPerformanceActual`];
  const perceivedKeys = [`${side}_GroundingPerformance_perceived`, `${side}_GroundingPerformancePerceived`];

  let actual: number | null = null;
  let perceived: number | null = null;

  for (const key of actualKeys) {
    const parsed = parseFloatString(formValues[key]);
    if (parsed !== null) {
      actual = parsed;
      break;
    }
  }
  for (const key of perceivedKeys) {
    const parsed = parseFloatString(formValues[key]);
    if (parsed !== null) {
      perceived = parsed;
      break;
    }
  }

  return { actual, perceived };
};

const parseFootPresentation = (value: unknown): string | null => {
  if (isBlank(value)) return null;
  return String(value).trim();
};

export function parseAssessmentValues(formValues: FormValues, version: RuleDocVersion = 'current'): ParsedValues {
  const numeric: Record<string, number | null> = {};
  const groundingMode = parseGroundingMode(formValues.GroundingMode);
  const grounding = {
    left: parseGroundingValueByMode(formValues, 'L', groundingMode),
    right: parseGroundingValueByMode(formValues, 'R', groundingMode),
  };
  const leftGrounding = parseGroundingDetailed(formValues, 'L');
  const rightGrounding = parseGroundingDetailed(formValues, 'R');
  const footPresentation = parseFootPresentation(formValues.FootPresentation);

  const assessmentByKey = getSopAssessmentByKey(version);

  Object.values(assessmentByKey).forEach((definition) => {
    const key = definition.key;
    if (key === 'GroundingPerformance' || key === 'FootPresentation' || key === 'InfrasternalAngle') {
      return;
    }

    ['L', 'R'].forEach((side) => {
      const prefix = `${side}_${key}`;
      const rawValue = formValues[prefix];

      numeric[prefix] = parseFloatString(rawValue);
    });
  });

  const infrasternalAngle = parseFloatString(formValues.InfrasternalAngle);

  return {
    numeric,
    groundingMode,
    grounding,
    groundingDetailed: {
      leftActual: leftGrounding.actual,
      rightActual: rightGrounding.actual,
      leftPerceived: leftGrounding.perceived,
      rightPerceived: rightGrounding.perceived,
    },
    footPresentation,
    infrasternalAngle,
  };
}
